import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
	CustomButton,
	CustomChips,
	CustomIconButton,
	CustomSwitch,
	CustomTooltip,
	icon,
	IconComponent,
	KnowledgeBasePopOver,
	Selector,
	UserAvatar,
} from "../../../../common/components";
import {
	isSegFeatureEnabled,
	SegFlags,
	translate,
	translateEnumProjectRoleDescription,
} from "../../../../common/providers";
import { NavUtils } from "../../../../navigation";
import { STATUSES } from "../../constants/constants";
import styles from "./MembersListRow.module.css";

export default function MembersListRow({
	allowAddRights = false,
	allowChangeRole = false,
	allowSendInvitation,
	isCurrentUser = false,
	onChangeLock,
	onChangeRole,
	onSendInvitation,
	projectId,
	roles = [],
	selectedCompanyIsMine,
	style,
	user,
}) {
	const navigate = useNavigate();
	const selectedRole = roles[roles.findIndex((r) => r.name === user.projectRole)] || "";
	const companyId = useSelector(({ context }) => context.company.id);
	const isPending = user.status === STATUSES.pending;
	const isBlocked = user.blocked || user.status === STATUSES.blocked;
	const canManageAccess = !isCurrentUser && allowChangeRole;
	const accessTooltip =
		(isCurrentUser && translate("team-management.members.access.tooltip.locked-self")) ||
		(!allowChangeRole && translate("team-management.members.access.tooltip.locked-rights")) ||
		(isBlocked && translate("team-management.members.access.tooltip.off")) ||
		translate("team-management.members.access.tooltip.on");
	const handleChangeRole = (e) => {
		onChangeRole({ user, role: e.target.value.name });
	};
	const tooltipTitle =
		(isCurrentUser && translate("team-management.members.selector.tooltip.own-role")) ||
		(!allowChangeRole &&
			((selectedCompanyIsMine && translate("team-management.members.selector.tooltip.not-allowed")) ||
				translate("team-management.members.selector.tooltip.must-be-in-company"))) ||
		"";
	return (
		<div className={styles["members-row"]} style={style}>
			<div className={styles["members-row__member-cell"]}>
				<UserAvatar
					companyId={user.companyId}
					displayName={user.displayName}
					email={user.email}
					firstName={user.firstname}
					lastName={user.lastname}
					projectId={projectId}
					size="xs"
					userId={user.id}
				/>
				<div className={styles["member-cell__identity"]}>
					<CustomTooltip showWhenOverflow title={user.displayName}>
						<span
							className={styles["member-cell__name"]}
							data-current={isCurrentUser}
							data-disabled={isBlocked}
							translate="no"
						>
							{user.displayName}
						</span>
					</CustomTooltip>
					<CustomTooltip showWhenOverflow title={user.email}>
						<span className={styles["member-cell__email"]} data-disabled={isBlocked} translate="no">
							{user.email}
						</span>
					</CustomTooltip>
				</div>
			</div>
			<div className={styles["members-row__select-cell"]}>
				{!isSegFeatureEnabled(SegFlags.PERMISSIONS) ? (
					<KnowledgeBasePopOver
						label={translate("team-management.members.selector.tooltip.not-allowed.by-flag")}
					>
						<div className={styles["select-cell__selector"]}>
							{Array.isArray(roles) && roles.length > 0 && (
								<Selector
									fullWidth
									autoTranslate="no"
									defaultValue=""
									disabled={!allowChangeRole || !isSegFeatureEnabled(SegFlags.PERMISSIONS)}
									field="displayName"
									items={roles}
									value={selectedRole}
									onChange={handleChangeRole}
								/>
							)}
						</div>
					</KnowledgeBasePopOver>
				) : (
					<CustomTooltip
						disableHoverListener={!isSegFeatureEnabled(SegFlags.PERMISSIONS)}
						title={tooltipTitle}
					>
						<div className={styles["select-cell__selector"]}>
							{Array.isArray(roles) && roles.length > 0 && (
								<Selector
									fullWidth
									autoTranslate="no"
									defaultValue=""
									disabled={!allowChangeRole}
									field="displayName"
									items={roles}
									value={selectedRole}
									onChange={handleChangeRole}
								/>
							)}
						</div>
					</CustomTooltip>
				)}
				<CustomTooltip
					autoTranslate="no"
					disableHoverListener={!isSegFeatureEnabled(SegFlags.PERMISSIONS)}
					title={
						(user.projectRole && translateEnumProjectRoleDescription(user.projectRole)) ||
						translate("team-management.members.no-description")
					}
				>
					<IconComponent
						className={styles["select-cell__info-icon"]}
						color="var(--text-disabled)"
						icon={icon.faInfoCircle}
						size="md"
					/>
				</CustomTooltip>
			</div>
			<div className={styles["members-row__rights-on-documents"]}>
				{user.companyId === companyId &&
					!user.blocked &&
					isSegFeatureEnabled(SegFlags.RIGHTS_ON_DOCUMENTS) &&
					(user.counters.rolesCount ? (
						<CustomTooltip
							title={
								user.counters.documentsCount === 1
									? translate("team-management.members.rights.tooltip.single")
									: translate("team-management.members.rights.tooltip.plural", {
											n: user.counters.documentsCount,
									  })
							}
						>
							<CustomChips
								color="neutral"
								label={String(user.counters.rolesCount)}
								showTooltip={false}
								size="sm"
								startIcon={icon.faFileLines}
								onClick={
									allowAddRights ? () => navigate(NavUtils.goToRightsOnDocuments(projectId)) : null
								}
							/>
						</CustomTooltip>
					) : (
						allowAddRights && (
							<CustomButton
								className={styles["add-rights-btn"]}
								color="secondary"
								variant="outlined"
								onClick={() => navigate(NavUtils.goToRightsOnDocuments(projectId))}
							>
								{translate("team-management.members.btn.add-right")}
							</CustomButton>
						)
					))}
			</div>
			<div
				className={styles["members-row__access-cell"]}
				data-testid={`manage.user.access.${user.displayName || user.email}`}
			>
				<div className={styles["access-cell__status"]}>
					{isPending ? (
						<CustomTooltip title={translate("team-management.members.status.pending.tooltip")}>
							<CustomChips
								color="warning"
								label={translate("team-management.members.status.pending")}
								showTooltip={false}
								size="sm"
								startIcon={icon.faClock}
							/>
						</CustomTooltip>
					) : (
						isBlocked && (
							<CustomChips
								color="error"
								label={translate("team-management.members.status.blocked")}
								size="sm"
								startIcon={icon.faBan}
							/>
						)
					)}
				</div>
				<div className={styles["access-cell__controls"]}>
					{isPending ? (
						<>
							{allowSendInvitation && (
								<CustomIconButton
									icon={icon.faPaperPlane}
									tooltip={translate("team-management.members.re-send-invitation.tooltip")}
									variant="outlined"
									onClick={() => onSendInvitation(user.id)}
								/>
							)}
							{canManageAccess && (
								<CustomIconButton
									icon={icon.faTimes}
									tooltip={translate("team-management.members.revoke-invitation.tooltip")}
									variant="outlined"
									onClick={() => onChangeLock(user)}
								/>
							)}
						</>
					) : (
						<CustomTooltip title={accessTooltip}>
							<span>
								<CustomSwitch
									checked={!isBlocked}
									disabled={!canManageAccess}
									size="small"
									onChange={() => onChangeLock(user)}
								/>
							</span>
						</CustomTooltip>
					)}
				</div>
			</div>
		</div>
	);
}
